import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateOrganization,
  getAdminGetOrganizationQueryKey,
} from '@/api/auth/admin/admin';
import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface OrganizationFlagsFormProps {
  organization: AdminOrganizationResponse;
}

/**
 * Edits the pricing-tier flags (IsNonProfit, IsPartner) via the admin update
 * endpoint. The server owns the invariants (non-profit/partner are mutually
 * exclusive; a partner cannot also carry a source partner); the UI only
 * disables the obviously-conflicting toggles and surfaces server errors.
 */
export function OrganizationFlagsForm({ organization }: OrganizationFlagsFormProps) {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useAdminUpdateOrganization({
    mutation: {
      onSuccess: () => {
        toast.success('Organization flags updated');
        queryClient.invalidateQueries({
          queryKey: getAdminGetOrganizationQueryKey(organization.id),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateOrganizationFlags', orgId: organization.id });
        toast.error('Failed to update flags. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const update = (next: { isPartner: boolean; isNonProfit: boolean }) =>
    mutate({ orgId: organization.id, data: next });

  const hasSourcePartner = !!organization.sourcePartnerId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing-tier Flags</CardTitle>
        <CardDescription>
          Non-profit and Partner are mutually exclusive. A partner organization cannot also have a
          source partner.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="isNonProfit">Non-profit</Label>
            <p className="text-sm text-muted-foreground">
              Classify this organization as a nonprofit.
            </p>
          </div>
          <Switch
            id="isNonProfit"
            checked={organization.isNonProfit}
            disabled={isPending || organization.isPartner}
            onCheckedChange={(checked) =>
              update({ isNonProfit: checked, isPartner: organization.isPartner })
            }
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="isPartner">Partner</Label>
            <p className="text-sm text-muted-foreground">
              Enroll this organization as a partner.
              {hasSourcePartner ? ' Disabled while a source partner is set.' : ''}
            </p>
          </div>
          <Switch
            id="isPartner"
            checked={organization.isPartner}
            disabled={isPending || organization.isNonProfit || hasSourcePartner}
            onCheckedChange={(checked) =>
              update({ isPartner: checked, isNonProfit: organization.isNonProfit })
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
